import { type FC, memo, useCallback, useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import Animated, { useSharedValue, useAnimatedStyle, withTiming, useDerivedValue } from "react-native-reanimated";
import { StyleSheet } from "react-native-unistyles";

export type RadioButtonProps = {
  selected: boolean;
  onSelect: (selected: boolean) => void;
  disabled?: boolean;
  error?: string;
};

const ContainerSize = {
  default: {
    width: 20,
    height: 20,
  },
};

const DotSize = {
  default: {
    width: 20,
    height: 20,
  },
};

const BorderWidthContainer = {
  default: 1,
};

const DotInnerSize = {
  default: {
    width: DotSize.default.width / 2 - BorderWidthContainer.default,
    height: DotSize.default.height / 2 - BorderWidthContainer.default,
  },
};

export const RadioButton: FC<RadioButtonProps> = memo(({ selected, onSelect, disabled = false, error }) => {
  const scale = useSharedValue(1);
  const isSelected = useSharedValue(selected);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const dotScale = useDerivedValue(() => {
    return withTiming(isSelected.value ? 1 : 0, { duration: 300 });
  });
  const dotOpacity = useDerivedValue(() => {
    return withTiming(isSelected.value ? 1 : 0, { duration: 300 });
  });

  const animatedContainerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const animatedContainerDotStyle = useAnimatedStyle(() => ({
    transform: [{ scale: dotScale.value }],
    opacity: dotOpacity.value,
  }));

  const animatedDotInnerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: dotScale.value }],
    opacity: dotOpacity.value,
  }));

  const handlePress = useCallback(() => {
    if (!disabled) {
      onSelect(!selected);
    }
  }, [selected, onSelect, disabled]);

  useEffect(() => {
    isSelected.value = selected;
  }, [selected, isSelected]);

  return (
    <View style={isFocused && styles.focused}>
      <Pressable
        onPress={handlePress}
        disabled={disabled}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={() => [
          styles.container,
          disabled && styles.disabled,
          error && styles.error,
          isHovered && styles.hovered,
          selected && styles.selected,
        ]}
      >
        <Animated.View style={animatedContainerStyle}>
          <Animated.View style={[styles.containerDot, animatedContainerDotStyle]}>
            <Animated.View style={animatedDotInnerStyle}>
              <View style={[styles.dotInner, disabled && styles.disabledDotInner]} />
            </Animated.View>
          </Animated.View>
        </Animated.View>
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    borderRadius: ContainerSize.default.width / 2,
    width: ContainerSize.default.width,
    height: ContainerSize.default.height,
    backgroundColor: "transparent",
    borderWidth: BorderWidthContainer.default,
    borderColor: theme.colors.field.border_default,
    justifyContent: "center",
    alignItems: "center",
    opacity: 1,
  },
  selected: {
    borderColor: theme.colors.interactive.controls.selected_midground,
  },
  containerDot: {
    justifyContent: "center",
    alignItems: "center",
  },
  dot: {
    backgroundColor: theme.colors.interactive.controls.selected_midground,
    width: DotSize.default.width,
    height: DotSize.default.height,
    borderRadius: DotSize.default.width / 2,
    position: "absolute",
  },
  dotInner: {
    width: DotInnerSize.default.width,
    height: DotInnerSize.default.height,
    borderRadius: DotInnerSize.default.width / 2,
    position: "absolute",
    top: -DotInnerSize.default.height / 2,
    left: -DotInnerSize.default.width / 2,
    backgroundColor: theme.colors.interactive.controls.selected_midground,
  },
  disabled: {
    borderColor: theme.colors.interactive.controls.disabled_background,
  },
  disabledDot: {
    backgroundColor: theme.colors.interactive.controls.disabled_background,
  },
  disabledDotInner: {
    backgroundColor: theme.colors.interactive.controls.disabled_active_secondary_background,
  },
  error: {
    borderColor: theme.colors.danger[500],
  },
  errorDot: {
    backgroundColor: theme.colors.danger[500],
  },
  hovered: {
    borderColor: theme.colors.interactive.controls.hover_midground,
  },
  hoveredDot: {
    backgroundColor: theme.colors.interactive.controls.hover_midground,
    borderColor: theme.colors.interactive.controls.hover_midground,
  },
  focused: {
    borderColor: theme.colors.interactive.controls.focused_border,
    borderWidth: 2,
    borderRadius: ContainerSize.default.width + BorderWidthContainer.default * 2 + theme.spacing.xxs / 2,
    width: ContainerSize.default.width + BorderWidthContainer.default * 2 + theme.spacing.xxs,
    height: ContainerSize.default.height + BorderWidthContainer.default * 2 + theme.spacing.xxs,
    justifyContent: "center",
    alignItems: "center",
  },
}));
